import { useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { isMobileApp } from "../utils/isMobileApp";
import { isSessionRefreshDue, refreshSessionOnce } from "../utils/loginUtils";

const ACTIVITY_THROTTLE_MS = 60 * 1000;

// Replaces the fixed timer: refresh while the user is present and the access token is near its end.
export function useSessionRefreshOnActivity(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let lastActivityCheck = 0;

    const evaluate = () => {
      if (disposed || document.visibilityState === "hidden") return;
      if (!isSessionRefreshDue()) return;
      refreshSessionOnce().catch((err) => {
        console.warn("[session] refresh on activity failed:", err);
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") evaluate();
    };
    const onFocus = () => evaluate();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityCheck < ACTIVITY_THROTTLE_MS) return;
      lastActivityCheck = now;
      evaluate();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pointerdown", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);

    let nativeHandle: { remove: () => Promise<void> } | null = null;
    if (isMobileApp()) {
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) evaluate();
      })
        .then((handle) => {
          if (disposed) handle.remove();
          else nativeHandle = handle;
        })
        .catch((err) => {
          console.warn("[session] appStateChange listener failed:", err);
        });
    }

    evaluate();

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      nativeHandle?.remove();
    };
  }, [enabled]);
}
